import { useCallback, useRef, useState } from 'react';

const OPENSEARCH_INDEXING_DELAY_MS = 2000;
const OPENSEARCH_RETRY_DELAY_MS = 2000;

/**
 * Hook that provides a delayed refetch mechanism for OpenSearch-backed views.
 *
 * After a bulk edit, OpenSearch needs time to index the changes. This hook:
 *   1. Waits 2s then refetches (primary attempt)
 *   2. Schedules a silent second refetch 2s later to catch any indexing misses
 *
 * @param refetch - The refetch function to call (e.g. from SearchDataContext)
 */
export function useDelayedRefetch(refetch: () => Promise<void> | void) {
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Always use a ref so callbacks call the latest refetch
  const refetchRef = useRef(refetch);
  refetchRef.current = refetch;

  const delayedRefetch = useCallback(async (): Promise<void> => {
    setIsRefreshing(true);
    try {
      // Wait for OpenSearch to index the changes before refetching
      await new Promise((r) => setTimeout(r, OPENSEARCH_INDEXING_DELAY_MS));
      await refetchRef.current();
    } catch {
      // Swallow refetch errors
    } finally {
      setIsRefreshing(false);
    }

    // Silent second refetch to catch any indexing misses
    setTimeout(() => {
      Promise.resolve(refetchRef.current()).catch(() => {});
    }, OPENSEARCH_RETRY_DELAY_MS);
  }, []); // Stable identity — uses ref internally

  return { delayedRefetch, isRefreshingAfterBulkEdit: isRefreshing };
}
